import React, { useState, useEffect } from "react";
import { useInView } from "react-intersection-observer";
import { useLocation, useNavigate } from "react-router-dom";
import { doc, onSnapshot } from "firebase/firestore";
import { db } from "../firebase";
import CustomAppBargd from "./guppydetailsAppbar";
import CustomAppBar from "./CustomAppBar";
import OrderDialog from "./OrderDialog";
import { FaHeart, FaShare, FaStar, FaTag, FaFish, FaCheck, FaGift, FaInfoCircle, FaShieldAlt, FaTruck } from 'react-icons/fa';

const GuppyDetails = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { guppy: initialGuppy = {} } = location.state || {};
  
  // State for guppy data
  const [guppy, setGuppy] = useState(initialGuppy);
  const [isFavorite, setIsFavorite] = useState(false);
   const [isLiked, setIsLiked] = useState(false);
  const [showShareTooltip, setShowShareTooltip] = useState(false);
  
  const {
    id,
    name = "Unnamed Guppy",
    sold_status = false,
    malevideo,
    femalevideo,
    maleimage,
    femaleimage,
    malecount = 0,
    femalecount = 0,
    couplecount = 0,
    maleprice = 0,
    femaleprice = 0,
    coupleprice = 0,
  } = guppy;

  const { ref, inView } = useInView({ triggerOnce: true, threshold: 0.5 });
  const [selectedGender, setSelectedGender] = useState("COUPLE");
  const [quantity, setQuantity] = useState({ MALE: 0, FEMALE: 0, COUPLE: 0 });
  const [showOrderDialog, setShowOrderDialog] = useState(false);

  // Set up real-time listener
  useEffect(() => {
    if (!id) return;

    const guppyRef = doc(db, "guppies", id);
    const unsubscribe = onSnapshot(guppyRef, (docSnapshot) => {
      if (docSnapshot.exists()) {
        const newData = docSnapshot.data();
        setGuppy(currentGuppy => ({
          ...currentGuppy,
          ...newData
        }));

        setQuantity(current => ({
          MALE: Math.min(current.MALE, newData.malecount || 0),
          FEMALE: Math.min(current.FEMALE, newData.femalecount || 0),
          COUPLE: Math.min(current.COUPLE, newData.couplecount || 0)
        }));
      }
    }, (error) => {
      console.error("Error listening to guppy updates:", error);
    });

    return () => unsubscribe();
  }, [id]);

  const getMaxQuantity = () => {
    switch (selectedGender) {
      case "MALE": return malecount;
      case "FEMALE": return femalecount;
      case "COUPLE": return couplecount;
      default: return 0;
    }
  };

  const getPrice = () => {
    switch (selectedGender) {
      case "MALE": return maleprice;
      case "FEMALE": return femaleprice;
      case "COUPLE": return coupleprice;
      default: return 0;
    }
  };

  const calculateTotalPrice = () => {
    const price = getPrice();
    let total = quantity[selectedGender] * price;

    if (selectedGender === "COUPLE" && quantity.COUPLE >= 5) {
      total = total * 0.9;
    }

    return total.toFixed(2);
  };

  const calculatesum = () => {
    const maleTotal = quantity.MALE * maleprice;
    const femaleTotal = quantity.FEMALE * femaleprice;
    let coupleTotal = quantity.COUPLE * coupleprice;
  
    if (quantity.COUPLE >= 5) {
      coupleTotal *= 0.9;
    }
  
    return (maleTotal + femaleTotal + coupleTotal).toFixed(2);
  };
  
  const handleIncreaseQuantity = () => {
    if (quantity[selectedGender] < getMaxQuantity()) {
      setQuantity((prev) => ({
        ...prev,
        [selectedGender]: prev[selectedGender] + 1,
      }));
    }
  };

  const handleDecreaseQuantity = () => {
    if (quantity[selectedGender] > 0) {
      setQuantity((prev) => ({
        ...prev,
        [selectedGender]: prev[selectedGender] - 1,
      }));
    }
  };

  const handleOrder = () => {
    if (quantity.MALE === 0 && quantity.FEMALE === 0 && quantity.COUPLE === 0) {
      alert("الكمية المطلوبة غير صالحة. يرجى اختيار كمية أكبر من 0.");
      return;
    }
    setShowOrderDialog(true);
  };

  const handleShare = () => {
    if (navigator.share) {
      navigator.share({
        title: `${name} - Clickfish DZ`,
        text: `تحقق من هذه السلالة الرائعة من أسماك الزينة: ${name}`,
        url: window.location.href,
      });
    } else {
      navigator.clipboard.writeText(window.location.href);
      setShowShareTooltip(true);
      setTimeout(() => setShowShareTooltip(false), 2000);
    }
  };

  const genderOptions = [
    { key: 'MALE', label: 'ذكر', icon: '♂️', color: '#06b6d4', count: malecount, price: maleprice },
    { key: 'FEMALE', label: 'أنثى', icon: '♀️', color: '#ec4899', count: femalecount, price: femaleprice },
    { key: 'COUPLE', label: 'زوج', icon: '💕', color: '#8b5cf6', count: couplecount, price: coupleprice }
  ];

  const totalItems = quantity.MALE + quantity.FEMALE + quantity.COUPLE;
  const hasDiscount = quantity.COUPLE >= 5;

  const selectionSummaryItems = [
    {
      key: "MALE",
      label: "ذكور",
      icon: "♂️",
      count: quantity.MALE,
      available: malecount,
      color: "#06b6d4",
    },
    {
      key: "FEMALE",
      label: "إناث",
      icon: "♀️",
      count: quantity.FEMALE,
      available: femalecount,
      color: "#ec4899",
    },
    {
      key: "COUPLE",
      label: "أزواج",
      icon: "💕",
      count: quantity.COUPLE,
      available: couplecount,
      color: "#a855f7",
    },
  ].filter((item) => item.count > 0);

  const careHighlights = [
    { label: "درجة الحرارة", value: "24° - 26°C", note: "المجال المثالي للجوبي" },
    { label: "تغيير الماء", value: "25% أسبوعياً", note: "يحافظ على نشاطها" },
    { label: "درجة الحموضة", value: "6.8 - 7.4 pH", note: "يحمي الألوان" },
  ];

  const experienceHighlights = [
    { title: "مستوى العناية", value: "سهل", detail: "مناسبة للمبتدئين" },
    { title: "نشاط السباحة", value: "عالي", detail: "تتحرك طوال اليوم" },
    { title: "تناغم الألوان", value: "+6 ألوان", detail: "تدرجات لامعة تحت الإضاءة" },
  ];

  const primarySelectionKey = (() => {
    if (quantity[selectedGender] > 0) return selectedGender;
    if (quantity.COUPLE > 0) return "COUPLE";
    if (quantity.MALE > 0) return "MALE";
    if (quantity.FEMALE > 0) return "FEMALE";
    return selectedGender;
  })();

  const primarySelectionCount = quantity[primarySelectionKey] || 0;
  useEffect(() => {
    const favorites = JSON.parse(localStorage.getItem('favorites') || '[]');
    setIsLiked(favorites.includes(guppy.id));
  }, [guppy.id]);

  const toggleFavorite = () => {
    const newLikedState = !isLiked;
    setIsLiked(newLikedState);
    
    // Update localStorage
    const favorites = JSON.parse(localStorage.getItem('favorites') || '[]');
    if (newLikedState) {
      if (!favorites.includes(guppy.id)) {
        localStorage.setItem('favorites', JSON.stringify([...favorites, guppy.id]));
      }
    } else {
      localStorage.setItem('favorites', JSON.stringify(favorites.filter(id => id !== guppy.id)));
    }
  };

  return (
    <div className="guppy-details-container">
      {/* Enhanced Animated Background */}
      <div className="animated-background"></div>

      <CustomAppBar onMenuClick={() => navigate("/")} />
      
      <div className="main-content">
        {/* Enhanced Hero Section */}
        <div className="hero-section">
          {/* Media Section */}
          <div className="media-container">
            <div ref={ref} className="media-wrapper">
              {/* Enhanced Status Badge */}
              <div className={`status-badge ${sold_status ? 'sold' : 'available'}`}>
                <div className="status-icon">
                  {sold_status ? '❌' : '✅'}
                </div>
                <span>{sold_status ? 'مباع' : 'متاح'}</span>
              </div>

              {/* Enhanced Action Buttons */}
              <div className="action-buttons">
        <button
    className={`heart-button ${isLiked ? 'liked' : ''}`}
    onClick={toggleFavorite}  // Changed from setIsLiked
    style={{
      position: "absolute",
      top: "16px",
      right: "16px",
      background: "rgba(0, 0, 0, 0.6)",
      border: "none",
      borderRadius: "50%",
      width: "44px",
      height: "44px",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      color: isLiked ? "#ef4444" : "white",
      cursor: "pointer",
      zIndex: 10,
    }}
  >
    <FaHeart size={18} />
  </button>
                <div className="share-container">
                  <button
                    onClick={handleShare}
                    className="action-btn share-btn"
                    aria-label="Share"
                  >
                    <FaShare />
                  </button>
                  {showShareTooltip && (
                    <div className="share-tooltip">
                      تم نسخ الرابط! ✅
                    </div>
                  )}
                </div>
              </div>

              {/* Enhanced Media Display */}
              <div className="media-display">
                {inView ? (
                  <video
                    src={selectedGender === "MALE" ? malevideo : selectedGender === "FEMALE" ? femalevideo : malevideo}
                    autoPlay
                    loop
                    muted
                    playsInline
                    poster={selectedGender === "MALE" ? maleimage : selectedGender === "FEMALE" ? femaleimage : maleimage}
                    className="media-video"
                  />
                ) : (
                  <img
                    src={selectedGender === "MALE" ? maleimage : selectedGender === "FEMALE" ? femaleimage : maleimage}
                    alt={name}
                    className="media-image"
                  />
                )}
                <div className="media-gradient-overlay"></div>
              </div>
            </div>

            {/* Enhanced Rating Section */}
    
          </div>

          {/* Enhanced Details Section */}
          <div className="details-section">
            {/* Enhanced Title & Price */}
            <div className="title-price-section">
              <div className="title-container">
                <h1 className="product-title">{name}</h1>
                <div className="title-underline"></div>
              </div>
              
              <div className="price-container">
                <div className="current-price">
                  {getPrice().toLocaleString()} DZD
                </div>
                <div className="price-badges">
                  <span className="special-price-badge">
                    <FaTag />
                    سعر مميز
                  </span>
                  <span className="quality-badge">
                    <FaShieldAlt />
                    جودة عالية
                  </span>
                </div>
              </div>
            </div>

            {/* Enhanced Features Grid */}
            <div className="features-grid">
              <div className="feature-card">
                <FaTruck className="feature-icon" />
                <div className="feature-content">
                  <h4>توصيل سريع</h4>
                  <p>خلال 24-48 ساعة</p>
                </div>
              </div>
              <div className="feature-card">
                <FaShieldAlt className="feature-icon" />
                <div className="feature-content">
                  <h4>ضمان الجودة</h4>
                  <p>أسماك صحية 100%</p>
                </div>
              </div>
              <div className="feature-card">
                <FaInfoCircle className="feature-icon" />
                <div className="feature-content">
                  <h4>استشارة مجانية</h4>
                  <p>نصائح العناية</p>
                </div>
              </div>
            </div>

            {/* Care Guidance */}
            <div className="care-section">
              <h3 className="section-title">دليل العناية السريع</h3>
              <div className="care-grid">
                {careHighlights.map((tip) => (
                  <div key={tip.label} className="care-card">
                    <div className="care-label">{tip.label}</div>
                    <div className="care-value">{tip.value}</div>
                    <div className="care-note">{tip.note}</div>
                  </div>
                ))}
              </div>
            </div>

            {/* Experience Highlights */}
            <div className="experience-section">
              {experienceHighlights.map((item) => (
                <div key={item.title} className="experience-card">
                  <div className="experience-title">{item.title}</div>
                  <div className="experience-value">{item.value}</div>
                  <div className="experience-detail">{item.detail}</div>
                </div>
              ))}
            </div>

            {/* Enhanced Stock Information */}
            <div className="stock-section">
              <h3 className="section-title">
                <FaFish />
                المتاح في المخزون
              </h3>
              <div className="stock-grid">
                {genderOptions.map(option => (
                  <div key={option.key} className={`stock-card ${option.count > 0 ? 'in-stock' : 'out-of-stock'}`}>
                    <div className="stock-icon" style={{ color: option.color }}>
                      {option.icon}
                    </div>
                    <div className="stock-label">{option.label}</div>
                    <div className="stock-count" style={{ color: option.count > 0 ? option.color : '#6b7280' }}>
                      {option.count}
                    </div>
                    <div className="stock-price">
                      {option.price.toLocaleString()} DZD
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Enhanced Gender Selection */}
            <div className="gender-section">
              <h3 className="section-title">اختر النوع:</h3>
              <div className="gender-options">
                {genderOptions.map(option => {
                  const isSelected = selectedGender === option.key;
                  const isAvailable = option.count > 0;
                  
                  return (
                    <button
                      key={option.key}
                      onClick={() => isAvailable && setSelectedGender(option.key)}
                      disabled={!isAvailable}
                      className={`gender-btn ${isSelected ? 'selected' : ''} ${!isAvailable ? 'disabled' : ''}`}
                      style={{
                        '--btn-color': option.color,
                        '--btn-color-alpha': `${option.color}40`
                      }}
                    >
                      <span className="gender-icon">{option.icon}</span>
                      <span className="gender-label">{option.label}</span>
                      <div className="gender-count">
                        {option.count}
                      </div>
                      <div className="gender-price">
                        {option.price.toLocaleString()} DZD
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Enhanced Quantity Selection */}
            <div className="quantity-section">
              <h3 className="section-title">الكمية:</h3>
              <div className="quantity-controls">
                <button 
                  onClick={handleDecreaseQuantity} 
                  disabled={quantity[selectedGender] === 0}
                  className="quantity-btn decrease"
                  aria-label="Decrease quantity"
                >
                  <span>−</span>
                </button>
                
                <div className="quantity-display">
                  <div className="quantity-value">
                    {quantity[selectedGender]}
                  </div>
                  <div className="quantity-label">
                    {genderOptions.find(opt => opt.key === selectedGender)?.label}
                  </div>
                </div>
                
                <button 
                  onClick={handleIncreaseQuantity} 
                  disabled={quantity[selectedGender] >= getMaxQuantity()}
                  className="quantity-btn increase"
                  aria-label="Increase quantity"
                >
                  <span>+</span>
                </button>
                
                <div className="available-info">
                  <div className="available-label">متاح</div>
                  <div className="available-count">
                    {getMaxQuantity()}
                  </div>
                </div>
              </div>
            </div>

            {/* Enhanced Special Offer Alert */}
            {selectedGender === "COUPLE" && quantity.COUPLE >= 3 && quantity.COUPLE < 5 && (
              <div className="special-offer-alert">
                <div className="offer-icon">
                  <FaGift />
                </div>
                <div className="offer-content">
                  <div className="offer-title">
                    أضف {5 - quantity.COUPLE} أزواج أخرى للحصول على خصم 10%! �
                  </div>
                  <div className="offer-savings">
                    وفر {((getPrice() * quantity.COUPLE * 0.1)).toLocaleString()} DZD
                  </div>
                </div>
                <div className="offer-pulse"></div>
              </div>
            )}
          </div>
        </div>

        {/* Enhanced Price Summary */}
        {totalItems > 0 && (
          <div className="price-summary-section">
            <h2 className="summary-title">ملخص الطلب</h2>

            <div className="summary-content">
              {/* Individual type pricing */}
              {genderOptions.map(option => {
                if (quantity[option.key] === 0) return null;
                
                const typeTotal = quantity[option.key] * option.price;
                const discountedTotal = option.key === 'COUPLE' && quantity.COUPLE >= 5 
                  ? typeTotal * 0.9 
                  : typeTotal;
                
                return (
                  <div key={option.key} className="summary-item">
                    <div className="item-info">
                      <span className="item-icon">{option.icon}</span>
                      <div className="item-details">
                        <div className="item-name">
                          {option.label} × {quantity[option.key]}
                        </div>
                        <div className="item-unit-price">
                          {option.price.toLocaleString()} DZD للواحد
                        </div>
                      </div>
                    </div>
                    
                    <div className="item-total">
                      {option.key === 'COUPLE' && quantity.COUPLE >= 5 ? (
                        <div className="discounted-price">
                          <div className="original-price">
                            {typeTotal.toLocaleString()} DZD
                          </div>
                          <div className="final-price">
                            {discountedTotal.toLocaleString()} DZD
                          </div>
                        </div>
                      ) : (
                        <div className="final-price">
                          {typeTotal.toLocaleString()} DZD
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}

              {/* Discount info */}
              {hasDiscount && (
                <div className="discount-applied">
                  <FaCheck />
                  <span>تم تطبيق خصم 10% على الأزواج! 🎉</span>
                </div>
              )}

              {/* Total */}
              <div className="summary-total">
                <div className="total-label">
                  إجمالي السعر ({totalItems} عنصر)
                </div>
                <div className="total-price">
                  {parseFloat(calculatesum()).toLocaleString()} DZD
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Enhanced Order Button */}
        <div className="order-section">
          <button
            onClick={handleOrder}
            disabled={totalItems === 0}
            className={`order-btn ${totalItems === 0 ? 'disabled' : 'active'}`}
          >
            <FaFish />
            <span>
              {totalItems === 0 ? 'اختر الكمية أولاً' : `اطلب الآن - ${parseFloat(calculatesum()).toLocaleString()} DZD`}
            </span>
            {totalItems > 0 && <div className="btn-shimmer"></div>}
          </button>
          
          {totalItems > 0 && (
            <div className="order-note">
              <FaTruck />
              <span>كليك و الحوتة بين يديك ClickfishDZ</span>
            </div>
          )}
        </div>
      </div>

      {/* Order Dialog */}
     {showOrderDialog && (
  <div className="dialog-overlay">
    <OrderDialog
      item={{
        ...guppy,
        type: "guppy",
        selectedQuantities: {
          MALE: quantity.MALE,
          FEMALE: quantity.FEMALE,
          COUPLE: quantity.COUPLE,
        },
        preferredGender: selectedGender
      }}
      onClose={() => setShowOrderDialog(false)}
    />
  </div>
)}
      {/* Enhanced Styles */}
      <style jsx>{`
        .guppy-details-container {
          min-height: 100vh;
          background: linear-gradient(135deg, #0f172a 0%, #1e293b 50%, #0f172a 100%);
          color: white;
          position: relative;
          overflow-x: hidden;
        }

        .animated-background {
          position: fixed;
          inset: 0;
          opacity: 0.1;
          background: radial-gradient(circle at 50% 50%, rgba(16, 185, 129, 0.2), rgba(59, 130, 246, 0.2));
          background-image: 
            linear-gradient(rgba(255,255,255,.1) 1px, transparent 1px),
            linear-gradient(90deg, rgba(255,255,255,.1) 1px, transparent 1px);
          background-size: 40px 40px;
          animation: backgroundMove 20s ease-in-out infinite;
          pointer-events: none;
          z-index: 0;
        }

        .main-content {
          position: relative;
          z-index: 10;
          max-width: 1200px;
          margin: 0 auto;
          padding: 24px 16px;
        }

        .hero-section {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(min(420px, 100%), 1fr));
          gap: 48px;
          margin-bottom: 48px;
          align-items: start;
        }

        .media-container {
          padding-top: 60px;
          position: relative;
        }

        .media-wrapper {
          position: relative;
          border-radius: 24px;
          overflow: hidden;
          border: 2px solid rgba(16, 185, 129, 0.3);
          box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.5);
          background: rgba(30, 41, 59, 0.5);
          backdrop-filter: blur(10px);
          transition: all 0.3s ease;
        }

        .media-wrapper:hover {
          border-color: rgba(16, 185, 129, 0.5);
          box-shadow: 0 35px 70px -12px rgba(16, 185, 129, 0.3);
          transform: translateY(-4px);
        }

        .status-badge {
          position: absolute;
          top: 20px;
          left: 20px;
          z-index: 20;
          display: flex;
          align-items: center;
          gap: 8px;
          padding: 12px 20px;
          border-radius: 25px;
          font-size: 14px;
          font-weight: bold;
          backdrop-filter: blur(15px);
          border: 1px solid rgba(255, 255, 255, 0.1);
          transition: all 0.3s ease;
        }

        .status-badge.available {
          background: linear-gradient(135deg, rgba(16, 185, 129, 0.9), rgba(5, 150, 105, 0.9));
          color: white;
          box-shadow: 0 8px 25px rgba(16, 185, 129, 0.3);
        }

        .status-badge.sold {
          background: linear-gradient(135deg, rgba(239, 68, 68, 0.9), rgba(220, 38, 38, 0.9));
          color: white;
          box-shadow: 0 8px 25px rgba(239, 68, 68, 0.3);
        }

        .status-icon {
          font-size: 16px;
          animation: pulse 2s infinite;
        }

        .action-buttons {
          position: absolute;
          top: 20px;
          right: 20px;
          z-index: 20;
          display: flex;
          gap: 12px;
        }

        .action-btn {
          width: 48px;
          height: 48px;
          background: rgba(0, 0, 0, 0.6);
          border: 1px solid rgba(255, 255, 255, 0.1);
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          backdrop-filter: blur(15px);
          transition: all 0.3s ease;
          color: white;
        }

        .action-btn:hover {
          background: rgba(0, 0, 0, 0.8);
          transform: scale(1.1);
          box-shadow: 0 8px 25px rgba(0, 0, 0, 0.4);
        }

        .favorite-btn.active {
          background: linear-gradient(135deg, #ef4444, #dc2626);
          box-shadow: 0 8px 25px rgba(239, 68, 68, 0.4);
        }

        .heart-icon {
          transition: all 0.3s ease;
        }

        .heart-icon.loved {
          color: #fff;
          animation: heartBeat 1s ease-in-out;
        }

        .share-container {
          position: relative;
        }

        .share-tooltip {
          position: absolute;
          top: 56px;
          right: 0;
          background: linear-gradient(135deg, #1e293b, #334155);
          color: white;
          padding: 10px 16px;
          border-radius: 12px;
          font-size: 13px;
          white-space: nowrap;
          animation: slideInDown 0.3s ease;
          box-shadow: 0 10px 25px rgba(0, 0, 0, 0.3);
          border: 1px solid rgba(255, 255, 255, 0.1);
        }

        .share-tooltip::before {
          content: '';
          position: absolute;
          top: -6px;
          right: 12px;
          width: 12px;
          height: 12px;
          background: #1e293b;
          border: 1px solid rgba(255, 255, 255, 0.1);
          border-bottom: none;
          border-right: none;
          transform: rotate(45deg);
        }

        .media-display {
          position: relative;
          overflow: hidden;
        }

        .media-video,
        .media-image {
          width: 100%;
          height: 420px;
          object-fit: cover;
          transition: transform 0.3s ease;
        }

        .media-wrapper:hover .media-video,
        .media-wrapper:hover .media-image {
          transform: scale(1.05);
        }

        .media-gradient-overlay {
          position: absolute;
          bottom: 0;
          left: 0;
          right: 0;
          height: 100px;
          background: linear-gradient(transparent, rgba(0, 0, 0, 0.7));
          pointer-events: none;
        }

        .rating-section {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 12px;
          margin-top: 24px;
          padding: 16px;
          background: rgba(30, 41, 59, 0.7);
          border-radius: 16px;
          backdrop-filter: blur(15px);
          border: 1px solid rgba(51, 65, 85, 0.5);
          flex-wrap: wrap;
        }

        .stars-container {
          display: flex;
          gap: 4px;
        }

        .star-icon {
          color: #fbbf24;
          font-size: 18px;
          filter: drop-shadow(0 2px 4px rgba(251, 191, 36, 0.3));
        }

        .rating-text {
          color: #cbd5e1;
          font-weight: 500;
        }

        .rating-badge {
          background: linear-gradient(135deg, #fbbf24, #f59e0b);
          color: black;
          padding: 4px 12px;
          border-radius: 20px;
          font-size: 12px;
          font-weight: bold;
        }

        .details-section {
          display: flex;
          flex-direction: column;
          gap: 32px;
        }

        .title-price-section {
          text-align: center;
        }

        .title-container {
          margin-bottom: 24px;
        }

        .product-title {
          font-size: clamp(2rem, 4vw, 3rem);
          font-weight: bold;
          margin-bottom: 12px;
          background: linear-gradient(135deg, #10b981, #06b6d4, #3b82f6);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
          line-height: 1.2;
        }

        .title-underline {
          width: 80px;
          height: 4px;
          background: linear-gradient(135deg, #10b981, #06b6d4);
          margin: 0 auto;
          border-radius: 2px;
        }

        .price-container {
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 16px;
        }

        .current-price {
          font-size: clamp(2rem, 4vw, 3rem);
          font-weight: bold;
          color: #10b981;
          text-shadow: 0 2px 4px rgba(16, 185, 129, 0.3);
        }

        .price-badges {
          display: flex;
          gap: 12px;
          flex-wrap: wrap;
          justify-content: center;
        }

        .special-price-badge,
        .quality-badge {
          display: flex;
          align-items: center;
          gap: 6px;
          padding: 8px 16px;
          border-radius: 25px;
          font-size: 14px;
          font-weight: 600;
          backdrop-filter: blur(10px);
          border: 1px solid rgba(255, 255, 255, 0.1);
        }

        .special-price-badge {
          background: rgba(16, 185, 129, 0.2);
          color: #10b981;
        }

        .quality-badge {
          background: rgba(59, 130, 246, 0.2);
          color: #60a5fa;
        }

        .features-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
          gap: 16px;
        }

       .feature-card {
          display: flex;
          align-items: center;
          gap: 16px;
          padding: 20px;
          background: rgba(30, 41, 59, 0.5);
          border-radius: 16px;
          border: 1px solid rgba(51, 65, 85, 0.5);
          backdrop-filter: blur(10px);
          transition: all 0.3s ease;
        }

        .care-section {
          background: rgba(30, 41, 59, 0.5);
          border-radius: 16px;
          padding: 24px;
          border: 1px solid rgba(51, 65, 85, 0.5);
          backdrop-filter: blur(10px);
        }

        .care-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(160px, 1fr));
          gap: 16px;
        }

        .care-card {
          background: rgba(15, 23, 42, 0.4);
          border-radius: 14px;
          padding: 16px;
          border: 1px solid rgba(51, 65, 85, 0.5);
          display: flex;
          flex-direction: column;
          gap: 6px;
        }

        .care-label {
          font-size: 13px;
          color: #94a3b8;
          text-transform: uppercase;
          letter-spacing: 1px;
        }

        .care-value {
          font-size: 18px;
          font-weight: 700;
          color: #10b981;
        }

        .care-note {
          font-size: 12px;
          color: #cbd5f5;
        }

        .experience-section {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(140px, 1fr));
          gap: 12px;
          margin-top: 20px;
        }

        .experience-card {
          background: rgba(16, 185, 129, 0.08);
          border: 1px solid rgba(16, 185, 129, 0.25);
          border-radius: 14px;
          padding: 16px;
          text-align: center;
          transition: transform 0.3s ease, box-shadow 0.3s ease;
        }

        .experience-card:hover {
          transform: translateY(-3px);
          box-shadow: 0 10px 25px rgba(16, 185, 129, 0.2);
        }

        .experience-title {
          font-size: 13px;
          color: #94a3b8;
        }

        .experience-value {
          font-size: 24px;
          font-weight: 700;
          color: #10b981;
        }

        .experience-detail {
          font-size: 12px;
          color: #cbd5f5;
        }

        .feature-card:hover {
          background: rgba(30, 41, 59, 0.7);
          border-color: rgba(16, 185, 129, 0.3);
          transform: translateY(-2px);
          box-shadow: 0 8px 25px rgba(0, 0, 0, 0.2);
        }

        .feature-icon {
          font-size: 24px;
          color: #10b981;
          flex-shrink: 0;
        }

        .feature-content h4 {
          font-size: 16px;
          font-weight: 600;
          color: white;
          margin: 0 0 4px 0;
        }

        .feature-content p {
          font-size: 14px;
          color: #94a3b8;
          margin: 0;
        }

        .stock-section {
          background: rgba(30, 41, 59, 0.5);
          border-radius: 16px;
          padding: 24px;
          border: 1px solid rgba(51, 65, 85, 0.5);
          backdrop-filter: blur(10px);
        }

        .section-title {
          display: flex;
          align-items: center;
          gap: 12px;
          font-size: 18px;
          font-weight: 600;
          color: white;
          margin: 0 0 20px 0;
        }

        .section-title svg {
          color: #10b981;
        }

        .stock-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(120px, 1fr));
          gap: 16px;
        }

        .stock-card {
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 8px;
          padding: 20px 16px;
          border-radius: 12px;
          transition: all 0.3s ease;
          position: relative;
          overflow: hidden;
        }

        .stock-card.in-stock {
          background: rgba(16, 185, 129, 0.1);
          border: 1px solid rgba(16, 185, 129, 0.3);
        }

        .stock-card.out-of-stock {
          background: rgba(107, 114, 128, 0.1);
          border: 1px solid rgba(107, 114, 128, 0.3);
        }

        .stock-card:hover.in-stock {
          background: rgba(16, 185, 129, 0.2);
          transform: translateY(-2px);
          box-shadow: 0 8px 25px rgba(16, 185, 129, 0.2);
        }

        .stock-icon {
          font-size: 24px;
          font-weight: bold;
        }

        .stock-label {
          font-size: 14px;
          font-weight: 600;
          color: white;
        }

        .stock-count {
          font-size: 18px;
          font-weight: bold;
        }

        .stock-price {
          font-size: 12px;
          color: #94a3b8;
          font-weight: 500;
        }

        .gender-section {
          background: rgba(30, 41, 59, 0.5);
          border-radius: 16px;
          padding: 24px;
          border: 1px solid rgba(51, 65, 85, 0.5);
          backdrop-filter: blur(10px);
        }

        .gender-options {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(160px, 1fr));
          gap: 12px;
        }

        .gender-btn {
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 8px;
          padding: 20px 16px;
          border-radius: 12px;
          border: 1px solid rgba(51, 65, 85, 0.5);
          background: rgba(51, 65, 85, 0.3);
          color: white;
          cursor: pointer;
          transition: all 0.3s ease;
          position: relative;
          overflow: hidden;
        }

        .gender-btn:hover:not(.disabled) {
          background: var(--btn-color-alpha);
          border-color: var(--btn-color);
          transform: translateY(-2px);
          box-shadow: 0 8px 25px rgba(0, 0, 0, 0.2);
        }

        .gender-btn.selected {
          background: var(--btn-color-alpha);
          border-color: var(--btn-color);
          box-shadow: 0 0 0 2px var(--btn-color);
        }

        .gender-btn.disabled {
          opacity: 0.5;
          cursor: not-allowed;
          background: rgba(107, 114, 128, 0.2);
          border-color: rgba(107, 114, 128, 0.3);
        }

        .gender-icon {
          font-size: 20px;
        }

        .gender-label {
          font-size: 14px;
          font-weight: 600;
        }

        .gender-count {
          font-size: 12px;
          background: rgba(255, 255, 255, 0.1);
          padding: 4px 8px;
          border-radius: 10px;
          font-weight: 600;
        }

        .gender-price {
          font-size: 12px;
          color: #94a3b8;
          font-weight: 500;
        }

        .quantity-section {
          background: rgba(30, 41, 59, 0.5);
          border-radius: 16px;
          padding: 24px;
          border: 1px solid rgba(51, 65, 85, 0.5);
          backdrop-filter: blur(10px);
        }

        .quantity-controls {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 16px;
          flex-wrap: wrap;
        }

        .quantity-btn {
          width: 48px;
          height: 48px;
          border-radius: 50%;
          border: 2px solid;
          background: transparent;
          color: white;
          font-size: 20px;
          font-weight: bold;
          cursor: pointer;
          transition: all 0.3s ease;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .quantity-btn.decrease {
          border-color: #ef4444;
          color: #ef4444;
        }

        .quantity-btn.increase {
          border-color: #10b981;
          color: #10b981;
        }

        .quantity-btn:hover:not(:disabled) {
          transform: scale(1.1);
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
        }

        
        .quantity-btn.decrease:hover:not(:disabled) {
          background: rgba(239, 68, 68, 0.2);
          box-shadow: 0 4px 12px rgba(239, 68, 68, 0.3);
        }

        .quantity-btn.increase:hover:not(:disabled) {
          background: rgba(16, 185, 129, 0.2);
          box-shadow: 0 4px 12px rgba(16, 185, 129, 0.3);
        }

        .quantity-btn:disabled {
          opacity: 0.3;
          cursor: not-allowed;
        }

        .quantity-display {
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 4px;
          min-width: 80px;
        }

        .quantity-value {
          font-size: 28px;
          font-weight: bold;
          color: #10b981;
          text-shadow: 0 2px 4px rgba(16, 185, 129, 0.3);
        }

        .quantity-label {
          font-size: 12px;
          color: #94a3b8;
          font-weight: 500;
        }

        .available-info {
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 4px;
        }

        .available-label {
          font-size: 12px;
          color: #94a3b8;
        }

        .available-count {
          font-size: 16px;
          font-weight: 600;
          color: #06b6d4;
        }

        .special-offer-alert {
          display: flex;
          align-items: center;
          gap: 16px;
          padding: 20px;
          background: linear-gradient(135deg, rgba(251, 191, 36, 0.2), rgba(245, 158, 11, 0.2));
          border: 1px solid rgba(251, 191, 36, 0.3);
          border-radius: 16px;
          position: relative;
          overflow: hidden;
          animation: pulse 2s infinite;
        }

        .offer-icon {
          font-size: 24px;
          color: #fbbf24;
          animation: bounce 2s infinite;
        }

        .offer-content {
          flex: 1;
        }

        .offer-title {
          font-size: 16px;
          font-weight: 600;
          color: white;
          margin-bottom: 4px;
        }

        .offer-savings {
          font-size: 14px;
          color: #fbbf24;
          font-weight: 500;
        }

        .offer-pulse {
          position: absolute;
          inset: 0;
          background: linear-gradient(135deg, rgba(251, 191, 36, 0.1), transparent);
          animation: pulse 3s infinite;
        }

        .price-summary-section {
          background: rgba(30, 41, 59, 0.8);
          border-radius: 16px;
          padding: 24px;
          margin-bottom: 24px;
          border: 1px solid rgba(51, 65, 85, 0.5);
          backdrop-filter: blur(15px);
          box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.3);
        }

        .summary-title {
          font-size: 20px;
          font-weight: bold;
          color: white;
          margin: 0 0 20px 0;
          text-align: center;
        }

        .summary-content {
          display: flex;
          flex-direction: column;
          gap: 16px;
        }

        .summary-item {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 16px;
          background: rgba(51, 65, 85, 0.3);
          border-radius: 12px;
          border: 1px solid rgba(51, 65, 85, 0.5);
        }

        .item-info {
          display: flex;
          align-items: center;
          gap: 12px;
        }

        .item-icon {
          font-size: 18px;
        }

        .item-details {
          display: flex;
          flex-direction: column;
          gap: 4px;
        }

        .item-name {
          font-size: 14px;
          font-weight: 600;
          color: white;
        }

        .item-unit-price {
          font-size: 12px;
          color: #94a3b8;
        }

        .item-total {
          text-align: right;
        }

        .discounted-price {
          display: flex;
          flex-direction: column;
          align-items: flex-end;
          gap: 4px;
        }

        .original-price {
          font-size: 12px;
          color: #94a3b8;
          text-decoration: line-through;
        }

        .final-price {
          font-size: 16px;
          font-weight: bold;
          color: #10b981;
        }

        .discount-applied {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 8px;
          padding: 12px;
          background: rgba(16, 185, 129, 0.2);
          border-radius: 12px;
          color: #10b981;
          font-weight: 600;
          font-size: 14px;
        }

        .summary-total {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 20px;
          background: rgba(16, 185, 129, 0.1);
          border: 2px solid rgba(16, 185, 129, 0.3);
          border-radius: 12px;
          margin-top: 8px;
        }

        .total-label {
          font-size: 16px;
          font-weight: 600;
          color: white;
        }

        .total-price {
          font-size: 24px;
          font-weight: bold;
          color: #10b981;
          text-shadow: 0 2px 4px rgba(16, 185, 129, 0.3);
        }

        .order-section {
          position: sticky;
          bottom: 0;
          z-index: 15;
          background: rgba(15, 23, 42, 0.95);
          backdrop-filter: blur(15px);
          border-top: 1px solid rgba(51, 65, 85, 0.5);
          padding: 20px;
          text-align: center;
        }

        .order-btn {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 12px;
          width: 100%;
          max-width: 400px;
          margin: 0 auto;
          padding: 16px 24px;
          border-radius: 50px;
          border: none;
          font-size: 16px;
          font-weight: bold;
          cursor: pointer;
          transition: all 0.3s ease;
          position: relative;
          overflow: hidden;
        }

        .order-btn.disabled {
          background: rgba(107, 114, 128, 0.3);
          color: #9ca3af;
          cursor: not-allowed;
        }

        .order-btn.active {
          background: linear-gradient(135deg, #10b981, #06b6d4);
          color: white;
          box-shadow: 0 8px 25px rgba(16, 185, 129, 0.4);
        }

        .order-btn.active:hover {
          transform: translateY(-2px);
          box-shadow: 0 12px 35px rgba(16, 185, 129, 0.5);
        }

        .btn-shimmer {
          position: absolute;
          inset: 0;
          background: linear-gradient(90deg, transparent, rgba(255,255,255,0.2), transparent);
          animation: shimmer 2s infinite;
        }

        .order-note {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 8px;
          margin-top: 12px;
          color: #94a3b8;
          font-size: 14px;
        }

        /* Enhanced Animations */
        @keyframes backgroundMove {
          0%, 100% { transform: translateX(0) translateY(0); }
          25% { transform: translateX(-10px) translateY(-5px); }
          50% { transform: translateX(10px) translateY(5px); }
          75% { transform: translateX(-5px) translateY(10px); }
        }

        @keyframes heartBeat {
          0%, 100% { transform: scale(1); }
          25% { transform: scale(1.2); }
          50% { transform: scale(1.1); }
          75% { transform: scale(1.15); }
        }

        @keyframes slideInDown {
          from {
            opacity: 0;
            transform: translateY(-10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.8; }
        }

        @keyframes bounce {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-10px); }
        }

        @keyframes shimmer {
          0% { transform: translateX(-100%); }
          100% { transform: translateX(100%); }
        }

        /* Responsive Design */
        @media (max-width: 1024px) {
          .hero-section {
            grid-template-columns: 1fr;
            gap: 32px;
          }

          .main-content {
            padding: 20px 12px;
          }

          .media-video,
          .media-image {
            height: 360px;
          }
        }

        @media (max-width: 768px) {
          .main-content {
            padding: 16px 8px;
          }

          .hero-section {
            gap: 24px;
          }

          .media-video,
          .media-image {
            height: 300px;
          }

          .features-grid {
            grid-template-columns: 1fr;
          }

          .stock-grid {
            grid-template-columns: repeat(auto-fit, minmax(100px, 1fr));
          }

          .gender-options {
            grid-template-columns: 1fr;
          }

          .quantity-controls {
            gap: 12px;
          }

          .product-title {
            font-size: 2rem;
          }

          .current-price {
            font-size: 2rem;
          }
        }

        @media (max-width: 640px) {
          .main-content {
            padding: 12px 4px;
          }

          .action-buttons {
            top: 12px;
            right: 12px;
            gap: 8px;
          }

          .action-btn {
            width: 40px;
            height: 40px;
          }

          .status-badge {
            top: 12px;
            left: 12px;
            padding: 8px 12px;
            font-size: 12px;
          }

          .rating-section {
            flex-direction: column;
            gap: 8px;
          }

          .price-badges {
            flex-direction: column;
            gap: 8px;
          }

          .summary-item {
            flex-direction: column;
            align-items: flex-start;
            gap: 8px;
          }

          .item-total {
            align-self: stretch;
            text-align: left;
          }

          .summary-total {
            flex-direction: column;
            gap: 8px;
            text-align: center;
          }

          .order-btn {
            font-size: 14px;
            padding: 14px 20px;
          }
        }

        @media (max-width: 480px) {
          .media-video,
          .media-image {
            height: 250px;
          }

          .feature-card {
            flex-direction: column;
            text-align: center;
            gap: 12px;
          }

          .stock-grid {
            grid-template-columns: repeat(3, 1fr);
          }

          .quantity-controls {
            flex-direction: column;
            gap: 16px;
          }

          .special-offer-alert {
            flex-direction: column;
            text-align: center;
            gap: 12px;
          }

          .dialog-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000; /* Higher than all other elements */
  backdrop-filter: blur(5px);
  animation: fadeIn 0.3s ease;
}

@keyframes fadeIn {
  from { opacity: 0; }
  to { opacity: 1; }
}
        }
      `}</style>
    </div>
  );
};

export default GuppyDetails;